import React, { useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../hooks/useAuth';
import { Button, Input, Label, Card, Select } from '../styles/components';
import { EnvironmentWatermark } from '../components/common/EnvironmentWatermark';

const LoginContainer = styled.div`
  min-height: 100vh;
  height: 100vh;
  width: 100vw;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${props => props.theme.colors.gray[50]};
  padding: ${props => props.theme.spacing['2xl']} ${props => props.theme.spacing.md};
  box-sizing: border-box;
  position: fixed;
  top: 0;
  left: 0;
  
  @media (min-width: 640px) {
    padding: ${props => props.theme.spacing['2xl']} ${props => props.theme.spacing.xl};
  }
  
  @media (min-width: 1024px) {
    padding: ${props => props.theme.spacing['2xl']} ${props => props.theme.spacing['2xl']};
  }
`;

const LoginCard = styled(Card)`
  max-width: 28rem;
  width: 100%;
  margin: 0 auto;
  flex-shrink: 0;
`;

const LoginHeader = styled.div`
  text-align: center;
  margin-bottom: ${props => props.theme.spacing['2xl']};
`;

const LoginTitle = styled.h2`
  margin-top: ${props => props.theme.spacing.xl};
  font-size: ${props => props.theme.fontSizes['3xl']};
  font-weight: ${props => props.theme.fontWeights.bold};
  color: ${props => props.theme.colors.gray[900]};
`;

const LoginSubtitle = styled.p`
  margin-top: ${props => props.theme.spacing.sm};
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.gray[600]};
`;

const LoginForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xl};
`;

const FormFields = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
`;

const ErrorMessage = styled.div`
  background-color: ${props => props.theme.colors.error[50]};
  border: 1px solid ${props => props.theme.colors.error[200]};
  color: ${props => props.theme.colors.error[700]};
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borders.radius.md};
`;

export function Login() {
  const [credentials, setCredentials] = useState({
    clientId: '',
    clientSecret: '',
    scope: 'api' as const,
  });
  const { login, isAuthenticated, isLoading, error } = useAuth();

  // Always redirect to clients page after login for consistent UX
  if (isAuthenticated) {
    return <Navigate to="/clients" replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await login(credentials);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setCredentials(prev => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  return (
    <LoginContainer>
      <LoginCard>
        <LoginHeader>
          <LoginTitle>
            Sign in to Financial Gateway
          </LoginTitle>
          <LoginSubtitle>
            Enter your client credentials to access the management portal
          </LoginSubtitle>
        </LoginHeader>
        
        <LoginForm onSubmit={handleSubmit}>
          <FormFields>
            <FormField>
              <Label htmlFor="scope">
                Login As
              </Label>
              <Select
                id="scope"
                name="scope"
                value={credentials.scope}
                onChange={handleChange}
              >
                <option value="api">API User (Organization Access)</option>
                <option value="support">Support User (Full Access)</option>
              </Select>
            </FormField>
            
            <FormField>
              <Label htmlFor="clientId">
                Client ID
              </Label>
              <Input
                id="clientId"
                name="clientId"
                type="text"
                autoComplete="username"
                required
                placeholder="Enter your client ID"
                value={credentials.clientId}
                onChange={handleChange}
              />
            </FormField>
            
            <FormField>
              <Label htmlFor="clientSecret">
                Client Secret
              </Label>
              <Input
                id="clientSecret"
                name="clientSecret"
                type="password"
                autoComplete="current-password"
                required
                placeholder="Enter your client secret"
                value={credentials.clientSecret}
                onChange={handleChange}
              />
            </FormField>
          </FormFields>

          {error && (
            <ErrorMessage>
              {error}
            </ErrorMessage>
          )}

          <Button
            type="submit"
            disabled={isLoading}
            style={{ width: '100%' }}
          >
            {isLoading ? 'Signing in...' : 'Sign in'}
          </Button>
        </LoginForm>
      </LoginCard>
      <EnvironmentWatermark />
    </LoginContainer>
  );
}